import type { CameraState } from '../domain/coordinates.ts';
import type { CameraMovement, InitialMapView, MapSession } from '../map/contracts.ts';
import type { SavedViewStore } from './saved-view.ts';

export interface CameraHashState {
  read(): CameraState | undefined;
  replace(camera: CameraState): void;
  subscribe(listener: (camera: CameraState) => void): () => void;
}

export interface CameraNavigation {
  initialView(fallback: InitialMapView): InitialMapView;
  connect(session: MapSession): () => void;
  cameraChanged(camera: CameraState, movement: CameraMovement): void;
}

export function createCameraNavigation({
  hash,
  saved,
}: {
  readonly hash: CameraHashState;
  readonly saved: SavedViewStore;
}): CameraNavigation {
  return Object.freeze({
    initialView(fallback: InitialMapView): InitialMapView {
      const camera = hash.read() ?? saved.load();
      return camera === undefined ? fallback : { type: 'camera', camera };
    },
    connect(session: MapSession): () => void {
      return hash.subscribe((camera) => {
        void session.execute({ type: 'set-camera', camera }).catch(() => undefined);
      });
    },
    cameraChanged(camera: CameraState, movement: CameraMovement): void {
      if (movement !== 'completed') return;
      saved.save(camera);
      hash.replace(camera);
    },
  });
}
